import { toast } from "react-toastify";
import { formatDate, parseAmount } from "../services/utils";
import useApiService from "./useApiService";

const useProject = () => {
  const { get, post, put, del } = useApiService();

  const fetchAllProjects = async () => {
    try {
      const response = await get("/api/projects");
      if (response.status && response.data) return { projects: response.data };
      else throw new Error("Unable to fetch projects");
    } catch (error) {
      return { error };
    }
  };

  const getAllProjectNames = async () => {
    const { projects, error } = await fetchAllProjects();
    if (error) return { error };
    return { projects: projects.map((project) => project.project_name) };
  };

  const getProjectNamesWithId = async () => {
    const { projects, error } = await fetchAllProjects();
    if (error) return { error };
    return { projects: projects.map((project) => ({ id: project.id, name: project.project_name })) };
  };

  const getProjectIdByName = async (name) => {
    const { projects, error } = await fetchAllProjects();
    if (error) return { error };
    const project = projects.find((project) => project.project_name === name);
    return { id: project.project_id };
  };

  const fetchAllProjectOwnersWithCommission = async () => {
    try {
      const response = await get("/api/project-owners");
      if (response.status && response.data) {
        const owners = response.data;
        return {
          owners: owners.map((owner) => ({
            ...owner,
            total_commission: parseAmount(owner.total_commission),
            total_payable_now: parseAmount(owner.total_payable_now),
            total_paid: parseAmount(owner.total_paid),
            total_payable_in_future_installments: parseAmount(owner.total_payable_in_future_installments),
          })),
        };
      } else throw new Error("Unable to fetch project owners");
    } catch (error) {
      return { error };
    }
  };

  const fetchProjectOwnerById = async (id) => {
    try {
      const response = await get(`/api/project-owner/${id}`);
      if (response.status && response.data) {
        const owner = response.data;
        return {
          owner: {
            owner: {
              id: id,
              name: owner.owner_name,
              contact: owner.owner_contact,
              email: owner.owner_email,
              age: owner.owner_age,
              gender: owner.owner_gender,
              address: owner.owner_address,
              occupation: owner.owner_occupation,
              photo: owner.owner_photo,
              aadhar_document_id: owner.owner_aadhar?.id,
              aadhar_document_url: owner.owner_aadhar?.url,
              pan_document_id: owner.owner_pan?.id,
              pan_document_url: owner.owner_pan?.url,
              photo_id: owner.owner_photo?.id,
              photo_url: owner.owner_photo?.url,
            },
            project: {
              name: owner.project_name,
              total_commission_due_emi_wise: parseAmount(owner.total_commission_due_emi_wise),
              total_amount_due_emi_wise: parseAmount(owner.total_amount_due_emi_wise),
              total_company_commission_due_emi_wise: parseAmount(owner.total_company_commission_due_emi_wise),
              total_amount_receivable_after_deductions: parseAmount(owner.total_amount_receivable_after_deductions),
              total_amount_receivable: parseAmount(owner.total_amount_receivable),
              total_commission_to_be_given: parseAmount(owner.total_commission_to_be_given),
              total_profit_after_deductions: parseAmount(owner.total_profit_after_deductions),
              development_charges_receivable: parseAmount(owner.development_charges_receivable),
              registration_charges_receivable: parseAmount(owner.registration_charges_receivable),
            },
            banks: owner.bank_details.map((bank) => ({
              name_in_bank: bank.name_in_bank,
              bank_name: bank.bank_name,
              bank_account_type: bank.bank_account_type,
              bank_account_number: bank.bank_account_number,
              bank_ifsc: bank.bank_ifsc,
              bank_branch: bank.bank_branch_name,
              bank_identification: bank.bank_identification,
            })),
            company: {
              company_id: owner.company_id,
              company_name: owner.company_name,
              company_address: owner.firm_address,
              logo: owner.company_logo.url,
            },
          },
        };
      } else throw new Error("Unable to fetch project owner");
    } catch (error) {
      return { error };
    }
  };

  const addNewPhase = async (projectId, phaseData) => {
    try {
      const response = await post(`/api/projects/${projectId}/phases`, phaseData);
      if (response.status) return { success: "Project phase created successfully." };
      else throw new Error("Error creating project phase");
    } catch (error) {
      return { error };
    }
  };

  const fetchAllTestimonials = async () => {
    try {
      const response = await get("/api/testimonial");
      if (response.status && response.data) return { testimonials: response.data };
      else throw new Error("Unable to fetch testimonials");
    } catch (error) {
      return { error };
    }
  };

  const fetchProjectById = async (id) => {
    try {
      const response = await get(`/api/projects/${id}`);
      if (response.status && response.data) {
        const {
          project_bank_details,
          aurus_commission_bank_details,
          registration_bank_details,
          development_bank_details,
        } = response.data;
        return {
          project: {
            project: {
              ...response.data,
              completion_eta: formatDate(response.data.completion_eta),
              emi_options: +response.data.emi_options.split(" ")[0],
            },
            projectBankDetails: project_bank_details && {
              ...project_bank_details,
              bank_branch: project_bank_details?.bank_branch_name,
              bank_identification_url: project_bank_details?.identification.url,
            },
            aurusCommissionBankDetails: aurus_commission_bank_details && {
              ...aurus_commission_bank_details,
              bank_branch: aurus_commission_bank_details?.bank_branch_name,
              bank_identification_url: aurus_commission_bank_details?.identification.url,
            },
            developmentBankDetails: development_bank_details && {
              ...development_bank_details,
              bank_branch: development_bank_details?.bank_branch_name,
              bank_identification_url: development_bank_details?.identification.url,
            },
            registrationBankDetails: registration_bank_details && {
              ...registration_bank_details,
              bank_branch: registration_bank_details?.bank_branch_name,
              bank_identification_url: registration_bank_details?.identification.url,
            },
          },
        };
      }
    } catch (error) {
      return { error };
    }
  };

  const updateProjectById = async (id, projectData) => {
    try {
      const response = await put(`/api/projects/${id}`, projectData);
      if (response.status) return { success: "Project updated successfully." };
      else throw new Error("Error updating project");
    } catch (error) {
      return { error };
    }
  };

  const getAllPlots = async () => {
    try {
      const response = await get("/api/plots");
      if (response.status && response.data) {
        return {
          plots: response.data.map((plot) => ({
            id: plot.project_id,
            project_name: plot.project_name,
            total_commission: parseAmount(plot.total_commission),
            total_withdrawable: parseAmount(plot.total_withdrawable),
            total_withdrawn: parseAmount(plot.total_withdrawn),
            total_upcoming_in_future_installments: parseAmount(plot.total_upcoming_in_future_installments),
            total_registration_charges: parseAmount(plot.total_registration_charges),
            total_development_charges: parseAmount(plot.total_development_charges),
            registration_charges_received: parseAmount(plot.registration_charges_received),
            development_charges_received: parseAmount(plot.development_charges_received),
          })),
        };
      } else throw new Error("Unable to fetch plots");
    } catch (error) {
      return { error };
    }
  };

  const getPlotDetails = async (projectId, phaseNumber, plotNumber) => {
    try {
      const response = await get(`/api/project/${projectId}/phase/${phaseNumber}/plot/${plotNumber}`);
      if (response.status && response.data) {
        return {
          plot: response.data,
        };
      }
      throw new Error("Unable to fetch plot");
    } catch (error) {
      return { error };
    }
  };

  const updatePlot = async (plotId, data) => {
    try {
      const response = await put(`/api/plot/${plotId}`, data);
      if (response.status) return { success: "Plot updated successfully." };
      else throw new Error("Error updating plot");
    } catch (error) {
      return { error };
    }
  };

  const deleteProjectById = async (id) => {
    try {
      const response = await del(`/api/projects/${id}`);
      if (response.status) return { success: "Project deleted successfully." };
      else throw new Error("Error deleting project");
    } catch (error) {
      return { error };
    }
  };

  const createNewProject = async (projectData) => {
    try {
      const response = await post("/api/project-new", projectData);
      if (response.status && response.project) {
        return { success: "Project created successfully.", project: response.project };
      } else throw new Error("Error creating project");
    } catch (error) {
      toast.error(error.message);
      return { error };
    }
  };

  const deleteTestimonialById = async (id) => {
    try {
      const response = await del(`/api/testimonial/${id}`);
      if (response.status) return { success: "Testimonial deleted successfully." };
      else throw new Error("Error deleting testimonial");
    } catch (error) {
      return { error };
    }
  };

  const addNewMedia = async (data) => {
    try {
      const response = await post(`/api/project-media`, data);
      if (response.status) {
        return { success: "Media added successfully." };
      } else throw new Error("Error adding media");
    } catch (error) {
      return { error };
    }
  };

  const getProjectMediaById = async (id) => {
    try {
      const response = await get(`/api/project/${id}/project-media`);
      if (response.status && response.data) {
        return {
          media: response.data.map((item) => ({
            id: item.id,
            media_title: item.media_title,
            media_type: item.file.file_type,
            url: item.file.url,
            file_id: item.file_id,
          })),
        };
      } else {
        throw new Error("Unable to fetch media");
      }
    } catch (error) {
      return { error };
    }
  };

  const updateProjectMediaById = async (id, data) => {
    try {
      const response = await put(`/api/project-media/${id}`, data);
      if (response.status) return { success: "Media updated successfully." };
      else throw new Error("Error updating media");
    } catch (error) {
      return { error };
    }
  };

  const updatePhaseById = async (projectId, phaseNumber, data) => {
    try {
      const response = await put(`/api/projects/${projectId}/phases/${phaseNumber}`, data);
      if (response.status) return { success: "Phase updated successfully." };
      else throw new Error("Error updating phase");
    } catch (error) {
      return { error };
    }
  };

  const getCustomersForPlots = async (projectId, phaseNumber, plotNumber) => {
    try {
      const response = await get(`/api/booking/customers/${projectId}/${phaseNumber}/${plotNumber}`);
      if (response.status && response.data) {
        return { customers: response.data };
      } else {
        throw new Error("Unable to fetch customer");
      }
    } catch (error) {
      return { error };
    }
  };

  const addTestimonial = async (data) => {
    try {
      const response = await post("/api/testimonial", data);
      if (response.status) return { success: "Testimonial added successfully." };
      else throw new Error("Error adding testimonial");
    } catch (error) {
      return { error };
    }
  };

  const editTestimonial = async (id, data) => {
    try {
      const response = await put(`/api/testimonial/${id}`, data);
      if (response.status) return { success: "Testimonial updated successfully." };
      else throw new Error("Error updating testimonial");
    } catch (error) {
      return { error };
    }
  };

  const addNewAmenity = async (data) => {
    try {
      const response = await post(`/api/amenities`, data);
      if (response.status) return { success: "Amenity added successfully." };
      else throw new Error("Error adding amenity");
    } catch (error) {
      return { error };
    }
  };

  const addNewStore = async (data) => {
    try {
      const response = await post(`/api/project/stores`, data);
      if (response.status) return { success: "Store added successfully." };
      else throw new Error("Error adding store");
    } catch (error) {
      return { error };
    }
  };

  return {
    addNewStore,
    addNewAmenity,
    editTestimonial,
    addTestimonial,
    fetchAllProjects,
    getAllProjectNames,
    fetchAllProjectOwnersWithCommission,
    fetchProjectOwnerById,
    addNewPhase,
    fetchAllTestimonials,
    fetchProjectById,
    getAllPlots,
    getProjectIdByName,
    getPlotDetails,
    updatePlot,
    deleteProjectById,
    createNewProject,
    deleteTestimonialById,
    updateProjectById,
    addNewMedia,
    getProjectMediaById,
    updatePhaseById,
    getCustomersForPlots,
    getProjectNamesWithId,
    updateProjectMediaById,
  };
};

export default useProject;
